import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Funnel, Plus, ChevronLeft, ChevronRight, Pencil, X, Check, Info, CircleCheck, GripVertical, Trash2 } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useHistory } from 'react-router-dom';
import apiHelper from '../../apiHelper';
import UnsavedChangesPrompt from '../../components/UnsavedChangesPrompt';
import DiscardConfirmationModal from '../../components/DiscardConfirmationModal';
import DeleteConfirmationModal from '../../components/DeleteConfirmationModal';
import AlertNotification from '../../components/AlertNotification';
import Pagination from '../../components/Pagination';
import SearchBox from '../../components/SearchBox';
import noBrandImage from '../../assets/img/brand/no-brand-img.png';
import emptySearchImg from '../../assets/img/brand/empty-search.png';
import { useLoader } from '../../components/GlobalLoader';

const PAGE_SIZE = 20;
const portalRoot = document.getElementById('portal-root');

// New Edit Confirmation Modal Component
const EditConfirmationModal = ({
  isOpen,
  onCancel,
  onConfirm,
  variationName = '',
}) => {
  if (!isOpen) return null;

  return (
    <>
      <div className="drawer-overlay" />
      <div
        className="entity-drawer modal-popup-cls"
        style={{
          maxWidth: 650,
          margin: "0 auto",
          background: "white",
          borderRadius: 12,
          zIndex: 1050,
          padding: '20px 25px',
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
          height: "auto",
          right: "auto",
          overflow: "inherit",
        }}
      >
        <div className=" d-flex justify-content-end align-items-center pt-0 pe-0">
          <button
            onClick={onCancel}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              outline: "none",
              height:"18px"
            }}
            onFocus={(e) => e.target.blur()}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
            >
              <path
                d="M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5"
                stroke="black"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
        <h3
          style={{
            textAlign: "left",
            color: "#212529",
            fontSize: "20px",
            fontWeight: "600",
            lineHeight: "24px",
            fontFamily: "Montserrat",
            marginBottom: "10px",
            paddingTop:'20px'
          }}
        >
          You are going to change the SKU Variation's text
        </h3>
        <p
          style={{
            marginBottom: "0",
            textAlign: "left",
            color: "#6C757D",
            fontSize: "14px",
            fontWeight: "400",
            lineHeight: "20px",
            fontFamily: "Montserrat",
            paddingBottom:'20px'
          }}
        >
          Once modified, all related texts in ERP will be updated (such as in SPU & SKU module).
        </p>
        <div className="drawer-footer" style={{ paddingTop: "10px",paddingBottom:'10px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            style={{
              display: "flex",
              height: "34px",
              minWidth: "100px",
              padding: "10px",
              justifyContent: "center",
              alignItems: "center",
              gap: "10px",
              borderRadius: "8px",
              border: "1px solid  var(--navbar-dropdown-active, #212529)",
              background: "var(--Background-White, #FFF)",
              boxShadow: "0 1px 4px 0 rgba(0, 0, 0, 0.10)",
              color: "var(--navbar-dropdown-active, #212529)",
              fontFamily: "Montserrat",
              fontSize: "12px",
              fontWeight: 600,
              fontStyle: "normal",
              lineHeight: "normal",
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            Cancel
          </button>

          <button
            onClick={onConfirm}
            style={{
              display: "flex",
              height: "34px",
              minWidth: "100px",
              padding: "10px",
              justifyContent: "center",
              alignItems: "center",
              gap: "10px",
              borderRadius: "8px",
              background: "var(--navbar-dropdown-active, #212529)",
              boxShadow: "0 1px 4px 0 rgba(0, 0, 0, 0.10)",
              color: "var(--Background-White, #FFF)",
              fontFamily: "Montserrat",
              fontSize: "12px",
              fontWeight: 600,
              fontStyle: "normal",
              lineHeight: "normal",
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            Confirm
          </button>
        </div>
      </div>
    </>
  );
};

const OptionPortal = ({ children, style }) =>
  createPortal(
    <div
      className="options-container main-company-body-inner"
      style={{ ...style, overflow: 'auto', zIndex: 2000, marginTop: '10px' }}
    >
      {children}
    </div>,
    portalRoot
  );

// Updated SortIcon component with fade effect based on direction
const SortIcon = ({ direction = 'none' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path 
      d="M8.59334 5.11111C8.9566 5.11111 9.13824 4.72222 8.87588 4.48148L6.29263 2.11111C6.13118 1.96296 5.86882 1.96296 5.70736 2.11111L3.12412 4.48148C2.86176 4.72222 2.86176 5.11111 3.40666 5.11111H8.59334Z" 
      fill={direction === 'asc' ? "#6C757D" : "#ADB5BD"}
      opacity={direction === 'asc' ? 1 : 0.5}
    />
    <path 
      d="M3.40666 6.88889C3.0434 6.88889 2.86176 7.27777 3.12412 7.51851L5.70736 9.88889C5.86882 10.037 6.13118 10.037 6.29264 9.88889L8.87588 7.51851C9.13824 7.27777 8.9566 6.88889 8.59334 6.88889H3.40666Z" 
      fill={direction === 'desc' ? "#6C757D" : "#ADB5BD"}
      opacity={direction === 'desc' ? 1 : 0.5}
    />
  </svg>
);

// Helper function to generate unique IDs for options
const generateOptionId = () => `opt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

export default function SkuPresets() {
  const history = useHistory();
  const breadcrumbs = ["Settings", "SKU Presets"];
  const { showLoader, hideLoader } = useLoader();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInputValue, setSearchInputValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [editingIds, setEditingIds] = useState([]); // Changed to array to track multiple edits
  const [rows, setRows] = useState([]);
  const [inputVariations, setInputVariations] = useState([]);
  const [error, setError] = useState(null);
  const [showPortal, setShowPortal] = useState(false);
  const [portalStyle, setPortalStyle] = useState({});
  const triggerRef = useRef(null);
  const [isAdding, setIsAdding] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [alertType, setAlertType] = useState("success");
  const [sortConfig, setSortConfig] = useState({ key: 'variation', direction: 'asc' });
  const [showExitModal, setShowExitModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const inputRefs = useRef({});
  const optionsInputRefs = useRef({});
  const [dragId, setDragId] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [showVariationChangeModal, setShowVariationChangeModal] = useState(false);
  const [showDeleteOptionModal, setShowDeleteOptionModal] = useState(false);
  const [optionToDelete, setOptionToDelete] = useState(null);
  const [originalVariation, setOriginalVariation] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [pendingCancel, setPendingCancel] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastData, setToastData] = useState({ type: 'success', recordName: 'SKU Variation', action: 'update' });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [variationToDelete, setVariationToDelete] = useState(null);
  const [pendingVariationId, setPendingVariationId] = useState(null);
  const [pendingNavigationPath, setPendingNavigationPath] = useState(null);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);
  const [showEditConfirmationModal, setShowEditConfirmationModal] = useState(false);
  const [originalVariations, setOriginalVariations] = useState({});
  const [allRows, setAllRows] = useState([]);
  const [newlyAddedRows, setNewlyAddedRows] = useState([]);
  const [preEditVariationName, setPreEditVariationName] = useState(null);
  const [newRowIds, setNewRowIds] = useState([]);
  const [originalRowOrder, setOriginalRowOrder] = useState([]);
  const [shouldApplySorting, setShouldApplySorting] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const prefetchCache = useRef(new Map());
  const prefetchInProgress = useRef(new Set());

  const [colWidths, setColWidths] = useState({
    variation: 180,
    options: 420,
  });

  const handleColResizeMouseDown = (e, key, startWidth, minWidth = 120) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const onMouseMove = (moveEvent) => {
      const delta = moveEvent.clientX - startX;
      setColWidths((prev) => ({ ...prev, [key]: Math.max(minWidth, startWidth + delta) }));
    };
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const ColResizeHandle = ({ colKey, minWidth }) => (
    <div
      className="col-resize-handle"
      onMouseDown={(e) => handleColResizeMouseDown(e, colKey, colWidths[colKey], minWidth)}
      onClick={(e) => e.stopPropagation()}
      style={{ position: 'absolute', right: 0, top: 0, height: '100%', width: '6px', cursor: 'col-resize', zIndex: 2, userSelect: 'none' }}
    />
  );

  // Track unsaved changes
  useEffect(() => {
    const hasChanges = inputVariations.some(v => v.variation.trim() || v.options.length > 0) || editingIds.length > 0;
    setHasUnsavedChanges(hasChanges);
    setIsDirty(hasChanges);
  }, [inputVariations, editingIds]);

  // Handle browser close warning
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return 'You have unsaved changes. Are you sure you want to leave?';
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Fixed navigation blocking - handle both old and new react-router versions
  useEffect(() => {
    const unblock = history.block((tx) => {
      if (hasUnsavedChanges) {
        let pathname;
        if (typeof tx === 'string') {
          pathname = tx;
        } else if (tx.pathname) {
          pathname = tx.pathname;
        } else if (tx.location) {
          pathname = tx.location.pathname;
        } else {
          pathname = window.location.pathname;
        }
        
        setPendingNavigationPath(pathname);
        setShowExitModal(true);
        return false; // Block navigation
      }
      return true; // Allow navigation
    });

    return () => unblock();
  }, [hasUnsavedChanges, history]);

  // Cleanup navigation state on unmount
  useEffect(() => {
    return () => {
      setPendingNavigationPath(null);
      setShowExitModal(false);
    };
  }, []);

  // Helper: format raw API items to local row shape
  const formatPresets = (items) =>
    items.map(item => ({
      id: item.sku_preset_id,
      variation: item.sku_preset_name,
      options: item.options
        .sort((a, b) => parseInt(a.sort_number || 0) - parseInt(b.sort_number || 0))
        .map(opt => ({
          id: opt.sku_preset_option_id,
          value: opt.sku_preset_option_name,
          data: opt,
        })),
      optionData: item.options.reduce((acc, opt) => {
        acc[opt.sku_preset_option_id] = opt;
        return acc;
      }, {}),
    }));

  // Build cache key for prefetch
  function buildSkuPresetCacheKey(page, search, sortCfg) {
    return JSON.stringify({ page, search: (search || '').trim(), sortKey: sortCfg.key, sortDir: sortCfg.direction });
  }

  // Ordering param for API
  function skuPresetOrderingParam(sortCfg) {
    const prefix = sortCfg.direction === 'desc' ? '-' : '';
    return `${prefix}${sortCfg.key}`;
  }

  // Silent background prefetch
  async function prefetchSkuPresetPage(page, search, sortCfg, count) {
    const totalPages = Math.ceil(count / PAGE_SIZE);
    if (page > totalPages) return;
    const key = buildSkuPresetCacheKey(page, search, sortCfg);
    if (prefetchCache.current.has(key) || prefetchInProgress.current.has(key)) return;
    prefetchInProgress.current.add(key);
    try {
      const params = new URLSearchParams({
        page,
        page_size: PAGE_SIZE,
        ordering: skuPresetOrderingParam(sortCfg),
      });
      if ((search || '').trim()) params.set('search', search.trim());
      const res = await apiHelper(`settings/sku-attributes/?${params}`, 'GET');
      if (res && res.ok) {
        const json = await res.json();
        prefetchCache.current.set(key, { formatted: formatPresets(json.results || []), count: json.count || 0 });
      }
    } catch (_) { /* silent */ } finally {
      prefetchInProgress.current.delete(key);
    }
  }

  // Clear cache when search or sort changes
  useEffect(() => {
    prefetchCache.current.clear();
    prefetchInProgress.current.clear();
  }, [searchTerm, sortConfig.key, sortConfig.direction]);

  // Server-side fetch — reactive on page/search/sort
  useEffect(() => {
    async function loadPresets() {
      const cacheKey = buildSkuPresetCacheKey(currentPage, searchTerm, sortConfig);
      const cached = prefetchCache.current.get(cacheKey);
      if (cached) {
        setRows(cached.formatted);
        setTotalCount(cached.count);
        setOriginalRowOrder(cached.formatted.map(r => r.id));
        setShouldApplySorting(true);
        prefetchSkuPresetPage(currentPage + 1, searchTerm, sortConfig, cached.count);
        return;
      }
      try {
        showLoader();
        const params = new URLSearchParams({
          page: currentPage,
          page_size: PAGE_SIZE,
          ordering: skuPresetOrderingParam(sortConfig),
        });
        if ((searchTerm || '').trim()) params.set('search', searchTerm.trim());
        const response = await apiHelper(`settings/sku-attributes/?${params}`, 'GET');
        if (!response || !response.ok) throw new Error('Fetch failed');
        const json = await response.json();
        const formatted = formatPresets(json.results || []);
        setRows(formatted);
        setTotalCount(json.count || 0);
        setOriginalRowOrder(formatted.map(r => r.id));
        setShouldApplySorting(true);
        prefetchSkuPresetPage(currentPage + 1, searchTerm, sortConfig, json.count || 0);
      } catch (error) {
        console.error('Error fetching variations:', error);
        setToastData({ type: 'error', recordName: 'SKU Variation', action: 'fetch' });
        setShowToast(true);
        setRows([]);
        setOriginalRowOrder([]);
      } finally {
        hideLoader();
      }
    }
    loadPresets();
  }, [currentPage, searchTerm, sortConfig]);

  // Update allRows whenever rows or inputVariations change
  useEffect(() => {
    // Combine saved rows with unsaved input variations (for validation purposes)
    const combinedRows = [
      ...rows,
      ...inputVariations
        .filter(v => !rows.some(r => r.id === v.id)) // Only include unsaved ones
        .map(v => ({
          id: v.id,
          variation: v.variation,
          options: v.options,
          optionData: v.optionData || {},
          isEditing: true // Mark as currently being edited
        }))
    ];
    setAllRows(combinedRows);
  }, [rows, inputVariations]);

  // Reset field errors when user types
  useEffect(() => {
    const handleInputChange = () => {
      setFieldErrors({});
    };

    // Add event listeners to all input fields
    Object.values(inputRefs.current).forEach(input => {
      if (input) {
        input.addEventListener('input', handleInputChange);
      }
    });

    return () => {
      Object.values(inputRefs.current).forEach(input => {
        if (input) {
          input.removeEventListener('input', handleInputChange);
        }
      });
    };
  }, [inputVariations]);

  // Handle column sorting - only for SKU Preset
  const handleSort = (key) => {
    if (key !== 'variation') return; // Only allow sorting for SKU Preset
    
    setSortConfig(prev => {
      if (prev.key === key) {
        if (prev.direction === 'asc') {
          return { key, direction: 'desc' };
        } else if (prev.direction === 'desc') {
          return { key, direction: 'asc' };
        }
      }
      return { key, direction: 'asc' };
    });
    
    // When user explicitly sorts, we should apply sorting
    setShouldApplySorting(true);
  };

  // Search submit/clear are handled by the shared <SearchBox> component.

  // Helper function to check if a row is being edited
  const isEditing = (rowId) => editingIds.includes(rowId);

  // Pin newly added rows at top; server handles search + sort for the rest
  const sortedFiltered = useMemo(() => {
    const newlyAdded = rows.filter(row => newlyAddedRows.includes(row.id));
    const existingRows = rows.filter(row => !newlyAddedRows.includes(row.id));
    return [...newlyAdded, ...existingRows];
  }, [rows, newlyAddedRows]);

  // Start Add or Edit - UPDATED: Allow multiple edits AND preserve existing errors
  const startEdit = (row = null) => {
    if (row) {
      // Check if this row is already being edited
      if (editingIds.includes(row.id)) {
        return; // Already editing this row, don't add duplicate
      }
      
      // Add to editingIds array
      setEditingIds(prev => [...prev, row.id]);
      
      // Store original variation name for this row
      setOriginalVariations(prev => ({
        ...prev,
        [row.id]: row.variation
      }));
      
      // Check if this row is already being edited in inputVariations
      const existingEdit = inputVariations.find(v => v.id === row.id);
      
      if (existingEdit) {
        // Already editing this row, don't add duplicate
        return;
      }
      
      setInputVariations(prev => [
        ...prev,
        { 
          id: row.id,
          variation: row.variation, 
          options: row.options.map(opt => ({
            id: opt.id,
            value: opt.value,
            data: opt.data
          })),
          optionData: row.optionData || {},
          newOptionInput: '' 
        }
      ]);
    } else {
      const newVariationId = Date.now();
      setInputVariations(prev => [
        ...prev,
        { id: newVariationId, variation: '', options: [], optionData: {}, newOptionInput: '' }
      ]);
      setNewRowIds(prev => [...prev, newVariationId]);
      setIsAdding(true);
    }
  };

  // Validate single preset with updated duplicate checking
  const validateVariation = (variation, variationId) => {
    const errors = {};
    
    // Check both SKU Variation and Variation Options for required validation
    const hasVariation = variation.variation.trim();
    const hasOptions = variation.options.length > 0;
    
    if (!hasVariation && !hasOptions) {
      // Show required errors for both fields
      errors.variation = 'SKU Variation is required';
      errors.options = 'Variation Options is required';
    } else if (!hasVariation) {
      errors.variation = 'SKU Variation is required';
    } else if (!hasOptions) {
      errors.options = 'Variation Options is required';
    }
    
    // Only check for duplicates if there's a variation name
    if (hasVariation) {
      // Check for duplicate preset names (case insensitive) - updated to check allRows
      const duplicateVariation = allRows.some(
        row => row.variation.toLowerCase() === variation.variation.trim().toLowerCase() && 
               row.id !== variationId // Check against other rows (both saved and unsaved)
      );
      
      if (duplicateVariation) {
        errors.variation = 'SKU Variation already exists';
        
      }
    }

    // Check for duplicate option values within the same preset (case insensitive)
    const optionValues = variation.options.map(opt => opt.value.toLowerCase());
    const uniqueValues = new Set(optionValues);
    const hasDuplicateOptions = optionValues.length !== uniqueValues.size;
    
    if (hasDuplicateOptions) {
      errors.options = 'There are duplicated values';
       setToastData({ 
        type: 'error', 
        recordName: 'SKU Variation',
        action: 'create'
      });
      setShowToast(true);
      // Find which values are duplicates
      const valueCounts = {};
      const duplicateOptions = [];
      
      variation.options.forEach(opt => {
        const lowerValue = opt.value.toLowerCase();
        valueCounts[lowerValue] = (valueCounts[lowerValue] || 0) + 1;
        if (valueCounts[lowerValue] > 1 && !duplicateOptions.includes(opt.value)) {
          duplicateOptions.push(opt.value);
        }
      });
      
      errors.duplicateOptions = duplicateOptions;
    }

    return errors;
  };

  // Validate SKU Variation field on blur
  const validateVariationOnBlur = (variationId) => {
    const variation = inputVariations.find(v => v.id === variationId);
    if (!variation) return;

    const errors = {};
    
    if (!variation.variation.trim()) {
      errors.variation = 'SKU Variation is required';
    } else {
      // Check for duplicate preset names (case insensitive) - updated to check allRows
      const duplicateVariation = allRows.some(
        row => row.variation.toLowerCase() === variation.variation.trim().toLowerCase() && 
               row.id !== variationId
      );
      
      if (duplicateVariation) {
        errors.variation = 'SKU Variation already exists';
      }
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(prev => ({
        ...prev,
        [variationId]: {
          ...prev[variationId],
          ...errors
        }
      }));
    }
  };

// Validate Variation Options on blur
const validateOptionsOnBlur = (variationId, updatedOptions = []) => {
  const variation = updatedOptions.length ? updatedOptions.find(v => v.id === variationId) : inputVariations.find(v => v.id === variationId);
  if (!variation) return;

  const errors = {};

  // Check if options are required
  if (variation.options.length === 0) {
    errors.options = 'Variation Options is required';
  }

  // Check for duplicate option values within the same preset (case insensitive)
  const optionValues = variation.options.map(opt => opt.value.toLowerCase());
  const uniqueValues = new Set(optionValues);
  const hasDuplicateOptions = optionValues.length !== uniqueValues.size;
  
  if (hasDuplicateOptions) {
    errors.options = 'There are duplicated values';
    // Find which values are duplicates
    const valueCounts = {};
    const duplicateOptions = [];
    
    variation.options.forEach(opt => {
      const lowerValue = opt.value.toLowerCase();
      valueCounts[lowerValue] = (valueCounts[lowerValue] || 0) + 1;
      if (valueCounts[lowerValue] > 1 && !duplicateOptions.includes(opt.value)) {
        duplicateOptions.push(opt.value);
      }
    });
    
    errors.duplicateOptions = duplicateOptions;
  }

  if (Object.keys(errors).length > 0) {
    setFieldErrors(prev => ({
      ...prev,
      [variationId]: {
        ...prev[variationId],
        ...errors
      }
    }));
  } else {
    // Clear options error if no duplicates
    setFieldErrors(prev => ({
      ...prev,
      [variationId]: {
        ...prev[variationId],
        options: null,
        duplicateOptions: null
      }
    }));
  }
};

  // Cancel specific edit
  const cancelSpecificEdit = (variationId) => {
    const variation = inputVariations.find(v => v.id === variationId);
    const hasChanges = variation && (variation.variation.trim() || variation.options.length > 0);
    
    if (isDirty || hasChanges) {
      setPendingVariationId(variationId);
      setShowDiscardModal(true);
    } else {
      performSpecificCancel(variationId);
    }
  };

  // Perform actual cancel for specific preset
  const performSpecificCancel = (variationId) => {
    // Remove from editingIds array
    setEditingIds(prev => prev.filter(id => id !== variationId));
    
    // Reset variation name to original if editing existing row
    const isExistingRow = rows.some(r => r.id === variationId);
    if (isExistingRow && originalVariations[variationId]) {
      // Find the original saved row to get the correct data
      const originalRow = rows.find(r => r.id === variationId);
      if (originalRow) {
        // Update the row in the table to show original data
        setRows(prev => prev.map(r => 
          r.id === variationId ? originalRow : r
        ));
      }
    }
    
    // Remove from inputVariations
    setInputVariations(prev => prev.filter(v => v.id !== variationId));
    
    // Check if this was an "add new" row (has no match in saved rows)
    const isNewRow = !rows.some(r => r.id === variationId) && newRowIds.includes(variationId);
    if (isNewRow) {
      // This was a new row being added, not an edit of existing row
      setNewRowIds(prev => prev.filter(id => id !== variationId));
      // Check if there are no more new rows being added
      const hasOtherNewRows = inputVariations.some(v => v.id !== variationId && newRowIds.includes(v.id));
      if (!hasOtherNewRows) {
        setIsAdding(false);
      }
    }
    
    // Clear original variation for this row
    setOriginalVariations(prev => {
      const newOrig = { ...prev };
      delete newOrig[variationId];
      return newOrig;
    });
    
    // Clear field errors for this variation
    setFieldErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[variationId];
      return newErrors;
    });
    
    // Update dirty state
    const remainingChanges = inputVariations.some(v => v.id !== variationId && (v.variation.trim() || v.options.length > 0));
    setIsDirty(remainingChanges);
    
    setShowDiscardModal(false);
    setPendingCancel(null);
    setPendingVariationId(null);
  };

  // Cancel option deletion
  const cancelDeleteOption = () => {
    setShowDeleteOptionModal(false);
    setOptionToDelete(null);
  };

  // Save specific preset - FIXED: Show proper error toast only on Save button click
  const saveSpecificVariation = async (variationId) => {
    const variation = inputVariations.find(v => v.id === variationId);
    if (!variation) return;
    
    // NEW: Check if there's text in newOptionInput that hasn't been converted to chip
    if (variation.newOptionInput && variation.newOptionInput.trim()) {
      setToastData({ 
        type: 'error', 
        recordName: 'SKU Variation',
        action: 'create'
      });
      setShowToast(true);
      return;
    }

    // Validate the specific preset - pass the variation.id for duplicate checking
    const variationErrors = validateVariation(variation, variationId);
    
    // NEW: Check if there are duplicate options and prevent saving
    if (variationErrors.duplicateOptions) {
      setFieldErrors(prev => ({
        ...prev,
        [variationId]: variationErrors
      }));
      
      setToastData({ 
        type: 'error', 
        recordName: 'SKU Variation',
        action: 'create'
      });
      setShowToast(true);
      return;
    }

    if (Object.keys(variationErrors).length > 0) {
      setFieldErrors(prev => ({
        ...prev,
        [variationId]: variationErrors
      }));
      
      // FIXED: Show proper error toast with correct format - ONLY ON SAVE BUTTON CLICK
      const isExistingRow = rows.some(r => r.id === variationId);
      setToastData({ 
        type: 'error', 
        recordName: 'SKU Variation',
        action: isExistingRow ? 'update' : 'create'
      });
      setShowToast(true);
      return;
    }

    // Show edit confirmation modal only when SKU Variation field has changed
    const isExistingRow = rows.some(r => r.id === variationId);
    const originalName = originalVariations[variationId];
    if (isExistingRow && variation.variation.trim() !== originalName) {
      // CHANGE 2: Store the current variation name before showing modal
      setPreEditVariationName(variation.variation.trim());
      setPendingVariationId(variationId);
      setShowEditConfirmationModal(true);
      return;
    }

    await performSpecificSave(variationId);
  };

  // Perform actual save for specific preset - UPDATED: Maintain position without sorting
  const performSpecificSave = async (variationId) => {
    const variation = inputVariations.find(v => v.id === variationId);
    if (!variation) return;

    // Prepare options with order information
    const optionsWithOrder = variation.options.map((option, index) => {
      const existingData = variation.optionData?.[option.id] || {};
      
      // Create clean option data
      const optionData = {
        sku_preset_option_name: option.value,
        sort_number: String(index + 1), // 1-based index for sort order
        is_case_sensitive: existingData.is_case_sensitive || 'no',
        remarks: existingData.remarks || '',
        sku_preset_option_abbreviation: existingData.sku_preset_option_abbreviation || ''
      };

      // Only include ID if it exists (for existing options) and it's a database ID (not temporary)
      if (existingData.sku_preset_option_id && typeof option.id === 'number') {
        optionData.sku_preset_option_id = existingData.sku_preset_option_id;
      }

      return optionData;
    });

    const payload = {
      sku_preset_name: variation.variation.trim(),
      options: optionsWithOrder,
    };

    try {
      const isExistingRow = rows.some(r => r.id === variationId);
      const endpoint = isExistingRow
        ? `settings/sku-attributes/update/${variationId}/`
        : 'settings/sku-attributes/create/';
      const method = 'POST';
      const response = await apiHelper(endpoint, method, payload);

      if (response.ok) {
        const data = await response.json();
        
        // Create optionData mapping for the new row
        const optionData = data.options.reduce((acc, opt) => {
          acc[opt.sku_preset_option_id] = opt;
          return acc;
        }, {});
        
        const newRow = {
          id: data.sku_preset_id,
          variation: data.sku_preset_name,
          options: data.options
            .sort((a, b) => parseInt(a.sort_number || 0) - parseInt(b.sort_number || 0))
            .map(opt => ({
              id: opt.sku_preset_option_id,
              value: opt.sku_preset_option_name,
              data: opt
            })),
          optionData: optionData
        };

        setRows(prev => {
          if (isExistingRow) {
            const updatedRows = prev.map(r => (r.id === variationId ? newRow : r));
            
            setShouldApplySorting(false);
            
            return updatedRows; // Keep the row at its original position
          } else {
            // For new row, add it at the top (beginning of array)
            const updatedRows = [newRow, ...prev];
            // Add to newlyAddedRows to keep it at top
            setNewlyAddedRows(prev => [...prev, data.sku_preset_id]);
            // Remove from newRowIds
            setNewRowIds(prev => prev.filter(id => id !== variationId));
            setTotalCount(prev => prev + 1);
            prefetchCache.current.clear();
            prefetchInProgress.current.clear();
            return updatedRows;
          }
        });

        // Only remove the specific preset that was saved from inputVariations
        setInputVariations(prev => prev.filter(v => v.id !== variationId));
        
        // Remove from editingIds array
        setEditingIds(prev => prev.filter(id => id !== variationId));
        
        // Reset states for this specific variation
        if (isExistingRow) {
          // Clear original variation for this row
          setOriginalVariations(prev => {
            const newOrig = { ...prev };
            delete newOrig[variationId];
            return newOrig;
          });
        }
        
        // Check if there are no more input variations
        const hasOtherEdits = inputVariations.some(v => v.id !== variationId);
        if (!hasOtherEdits) {
          setIsAdding(false);
        }

        setFieldErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[variationId];
          return newErrors;
        });
        
        // Show success toast - FIXED: Use proper format
        setToastData({ 
          type: 'success', 
          recordName: 'SKU Variation',
          action: isExistingRow ? 'update' : 'create'
        });
        setShowToast(true);
        
        setShowVariationChangeModal(false);
        setShowEditConfirmationModal(false);
        
        // Update dirty state
        const remainingChanges = inputVariations.some(v => v.id !== variationId && (v.variation.trim() || v.options.length > 0));
        setIsDirty(remainingChanges);
        
        setPendingVariationId(null);
        setPreEditVariationName(null); // Clear the pre-edit name
      } else {
        const error = await response.json();
        console.error("Save error:", error);
        // FIXED: Show proper error message with proper format - ONLY ON SAVE BUTTON CLICK
        const isExistingRow = rows.some(r => r.id === variationId);
        setToastData({ 
          type: 'error', 
          recordName: 'SKU Variation',
          action: isExistingRow ? 'update' : 'create'
        });
        setShowToast(true);
      }
    } catch (error) {
      console.error("Save error:", error);
      // FIXED: Show proper error message with proper format - ONLY ON SAVE BUTTON CLICK
      const isExistingRow = rows.some(r => r.id === variationId);
      setToastData({ 
        type: 'error', 
        recordName: 'SKU Variation',
        action: isExistingRow ? 'update' : 'create'
      });
      setShowToast(true);
    }
  };

  // Handle edit confirmation
  const confirmEdit = async () => {
    if (!pendingVariationId) return;
    await performSpecificSave(pendingVariationId);
  };

  // Handle edit cancellation - reset to original name
  const cancelEdit = () => {
    if (pendingVariationId && preEditVariationName !== null) {
      // CHANGE 2: Reset to the name before the edit confirmation modal was shown
      const originalName = originalVariations[pendingVariationId];
      if (originalName) {
        // Reset the variation name in inputVariations to the original name
        setInputVariations(prev => 
          prev.map(v => 
            v.id === pendingVariationId 
              ? { ...v, variation: originalName }
              : v
          )
        );
      }
    }
    setShowEditConfirmationModal(false);
    setPendingVariationId(null);
    setPreEditVariationName(null); // Clear the pre-edit name
  };

  // Handle preset change confirmation
  const confirmVariationChange = async () => {
    if (!pendingVariationId) return;

    // Update all relevant SPU & SKU references
    const variation = inputVariations.find(v => v.id === pendingVariationId);
    if (!variation) return;

    const originalName = originalVariations[pendingVariationId];
    const payload = {
      old_preset: originalName,
      new_preset: variation.variation.trim(),
      options: variation.options.map(opt => opt.value)
    };
    
    const response = await apiHelper(`settings/sku-attributes/update-references/${pendingVariationId}/`, 'POST', payload);
    
    if (response.ok) {
      await performSpecificSave(pendingVariationId);
    } else {
      setToastData({ 
        type: 'error', 
        recordName: 'SKU Variation',
        action: 'update'
      });
      setShowToast(true);
    }
  };

  // Handle preset change cancellation
  const cancelVariationChange = () => {
    if (pendingVariationId) {
      const originalName = originalVariations[pendingVariationId];
      if (originalName) {
        setInputVariations(prev => 
          prev.map(v => {
            if (v.id === pendingVariationId) {
              return { ...v, variation: originalName };
            }
            return v;
          })
        );
      }
    }
    setShowVariationChangeModal(false);
    setIsDirty(true);
    setPendingVariationId(null);
  };

  // Handle delete preset click
  const handleDeleteVariation = (rowId) => {
    setVariationToDelete(rowId);
    setShowDeleteConfirmationModal(true);
  };

  // Confirm delete preset - UPDATED: Changed endpoint
  const confirmDeleteVariation = async () => {
    if (!variationToDelete) return;

    try {
      showLoader();
      const response = await apiHelper(`settings/sku-attributes/${variationToDelete}/delete/`, 'DELETE');
      
      if (response.ok) {
        setRows(prev => prev.filter(row => row.id !== variationToDelete));
        setTotalCount(prev => Math.max(0, prev - 1));
        prefetchCache.current.clear();
        prefetchInProgress.current.clear();
        // Also remove from inputVariations if being edited
        setInputVariations(prev => prev.filter(v => v.id !== variationToDelete));
        // Remove from editingIds if it was being edited
        setEditingIds(prev => prev.filter(id => id !== variationToDelete));
        // Remove from newlyAddedRows if it was a newly added row
        setNewlyAddedRows(prev => prev.filter(id => id !== variationToDelete));
        // Remove from newRowIds if it was a new row being added
        setNewRowIds(prev => prev.filter(id => id !== variationToDelete));
        // Clear original variation
        setOriginalVariations(prev => {
          const newOrig = { ...prev };
          delete newOrig[variationToDelete];
          return newOrig;
        });
        // Clear field errors
        setFieldErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[variationToDelete];
          return newErrors;
        });
        // Removed toast for delete success event
      } else {
        const error = await response.json();
        console.error("Delete error:", error);
        setToastData({ 
        type: 'error', 
        recordName: 'SKU Variation',
        action: 'delete'
        });
        setShowToast(true);
      }
    } catch (error) {
      console.error("Delete error:", error);
      setToastData({ 
        type: 'error', 
        recordName: 'SKU Variation',
        action: 'delete'
      });
      setShowToast(true);
    } finally {
      hideLoader();
      setShowDeleteConfirmationModal(false);
      setVariationToDelete(null);
    }
  };

  // Cancel delete preset
  const cancelDeleteVariation = () => {
    setShowDeleteConfirmationModal(false);
    setVariationToDelete(null);
  };

  // FIXED: Handle navigation confirmation
  const confirmNavigation = () => {
    // Clear all unsaved changes
    setInputVariations([]);
    setEditingIds([]);
    setIsAdding(false);
    setOriginalVariation(null);
    setOriginalVariations({});
    setFieldErrors({});
    setNewRowIds([]);
    setIsDirty(false);
    setHasUnsavedChanges(false);
    
    // Close the modal
    setShowExitModal(false);
    
    // Use setTimeout to ensure state updates are processed before navigation
    setTimeout(() => {
      if (pendingNavigationPath) {
        history.push(pendingNavigationPath);
      }
      setPendingNavigationPath(null);
    }, 0);
  };

  // FIXED: Handle navigation cancellation
  const cancelNavigation = () => {
    setShowExitModal(false);
    setPendingNavigationPath(null);
  };

// UPDATED: Enhanced addNewOption - use unique IDs for each option
const addNewOption = (variationId) => {
  setInputVariations(prev => {
    const variation = prev.find(v => v.id === variationId);
    const label = variation.newOptionInput.trim();
    if (!label) {
      return prev;
    }
  
    // Create new option with unique ID
    const newOptionId = generateOptionId();
    const newOption = {
      id: newOptionId,
      value: label,
      data: {
        sku_preset_option_name: label,
        sort_number: String(variation.options.length + 1),
        is_case_sensitive: 'no',
        remarks: '',
        sku_preset_option_abbreviation: ''
      }
    };

    const updatedOptionData = {
      ...variation.optionData,
      [newOptionId]: newOption.data
    };

    const updatedVariations = prev.map(v =>
      v.id === variationId
        ? { 
            ...v, 
            options: [...v.options, newOption], 
            optionData: updatedOptionData,
            newOptionInput: '' 
          }
        : v
    );

    // Check for duplicates after adding
    const updatedVariation = updatedVariations.find(v => v.id === variationId);
    const optionValues = updatedVariation.options.map(opt => opt.value.toLowerCase());
    const uniqueValues = new Set(optionValues);
    const hasDuplicateOptions = optionValues.length !== uniqueValues.size;
    
    if (hasDuplicateOptions) {
      setFieldErrors(prev => ({
        ...prev,
        [variationId]: {
          ...prev[variationId],
          options: 'There are duplicated values',
          duplicateOptions: Array.from(optionValues.filter((value, index, self) => self.indexOf(value) !== index))
        }
      }));
    }

    return updatedVariations;
  });
  setIsDirty(true);
};

 // UPDATED: Remove option from preset with unique ID - with proper error clearing
const removeOptionFromVariation = (variationId, optionId) => {
  setInputVariations(prev => {
    const updatedVariations = prev.map(v => {
      if (v.id === variationId) {
        const newOptionData = { ...v.optionData };
        delete newOptionData[optionId];
        
        const newOptions = v.options.filter(opt => opt.id !== optionId);
        
        return {
          ...v,
          options: newOptions,
          optionData: newOptionData
        };
      }
      return v;
    });
    
    // Check if removing this option resolved duplicate issues
    const updatedVariation = updatedVariations.find(v => v.id === variationId);
    if (updatedVariation) {
      const optionValues = updatedVariation.options.map(opt => opt.value.toLowerCase());
      const uniqueValues = new Set(optionValues);
      const hasDuplicateOptions = optionValues.length !== uniqueValues.size;
      
      if (!hasDuplicateOptions && fieldErrors[variationId]?.options === 'There are duplicated values') {
        // Clear the duplicate error
        setFieldErrors(prev => ({
          ...prev,
          [variationId]: {
            ...prev[variationId],
            options: null,
            duplicateOptions: null
          }
        }));
      }
    }
    
    return updatedVariations;
  });
  
  setIsDirty(true);
};

  // FIXED: Direct option removal using unique ID
  const handleOptionRemove = (variationId, optionId) => {
    removeOptionFromVariation(variationId, optionId);
  };

  const handlePortalPosition = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPortalStyle({
        position: 'absolute',
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
        background: '#fff',
        borderRadius: '8px',
        maxHeight: '300px',
        border: '1px solid #6c757d',
        marginTop: '4px',
        overflowY: 'auto',
        boxShadow: '0px 1px 4px 0px #0000001A',
      });
      setShowPortal(true);
    }
  };

  useEffect(() => {
    const handleClickOutside = e => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target) &&
        portalRoot && !portalRoot.contains(e.target)
      ) {
        setShowPortal(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', handlePortalPosition);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handlePortalPosition);
    };
  }, []);

  const saveRef = useRef(null);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const handleSaveConfirm = () => {
    setSaveModalOpen(false);
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (saveRef.current && !saveRef.current.contains(event.target)) {
        setSaveModalOpen(false);
      }
    }
    if (saveModalOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [saveModalOpen]);

  // Drag and Drop Handlers for Options - UPDATED: Use unique IDs
  const handleOptionDragStart = (e, variationId, optionId) => {
    setDragId(`${variationId}-${optionId}`);
    e.dataTransfer.setData('text/plain', `${variationId}-${optionId}`);
  };

  const [dragOverIndex, setDragOverIndex] = useState(null);

// 1. Track where the dragged item is hovering
// const handleOptionDragOver = (e) => {
//   e.preventDefault();
//   e.dataTransfer.dropEffect = 'move';

//   const container = e.currentTarget;
//   if (!container) return;

//   const chips = Array.from(container.querySelectorAll('.tag[draggable="true"]'));
//   const input = container.querySelector('input[type="text"]');

//   // Safely check if we're over the input field
//   const isOverInput = input && (e.target === input || input.contains(e.target));
//   if (isOverInput) {
//     setDragOverIndex(null);
//     return;
//   }

//   // If no chips, drop at end
//   if (chips.length === 0) {
//     setDragOverIndex(0);
//     return;
//   }

//   let closestIndex = -1;
//   let minDistance = Infinity;

//   chips.forEach((chip, index) => {
//     const rect = chip.getBoundingClientRect();
//     const chipCenterX = rect.left + rect.width / 2;
//     const distance = Math.abs(e.clientX - chipCenterX);

//     if (distance < minDistance) {
//       minDistance = distance;
//       closestIndex = index;

//       // Determine if we're on the left or right half of the chip
//       const isLeftHalf = e.clientX < chipCenterX;
//       setDragOverIndex(isLeftHalf ? index : index + 1);
//     }
//   });

//   // Fallback: if somehow no chip is closest, allow drop at end
//   if (closestIndex === -1) {
//     setDragOverIndex(chips.length);
//   }
// };

const handleOptionDragOver = (e) => {
  e.preventDefault();

  const container = e.currentTarget;
  if (!container) return;

  const chips = [
    ...container.querySelectorAll('.tag[draggable="true"]')
  ];

  if (!chips.length) {
    setDragOverIndex(0);
    return;
  }

  const mouseX = e.clientX;
  const mouseY = e.clientY;

  let closest = {
    offset: Number.NEGATIVE_INFINITY,
    index: chips.length,
  };

  chips.forEach((chip, index) => {
    const rect = chip.getBoundingClientRect();

    const offset =
      mouseY - rect.top - rect.height / 2;

    // Only consider chips in same vertical zone
    if (mouseY >= rect.top && mouseY <= rect.bottom) {
      const horizontalOffset =
        mouseX - rect.left - rect.width / 2;

      if (horizontalOffset < 0 && horizontalOffset > closest.offset) {
        closest = {
          offset: horizontalOffset,
          index: index,
        };
      }
    }
  });

  if (closest.index === chips.length) {
    // If not found in same row, place at end
    setDragOverIndex(chips.length);
  } else {
    setDragOverIndex(closest.index);
  }
};

// 2. Handle drop with correct insertion index - UPDATED: Use unique IDs
const handleOptionDrop = (e, variationId) => {
  e.preventDefault();
  e.stopPropagation();
  
  const dragData = e.dataTransfer.getData('text/plain');
  if (!dragData) return;
  
  const [dragVariationId, draggedOptionId] = dragData.split('-');
  if (dragVariationId !== String(variationId)) return;
  
  setInputVariations(prev => {
    return prev.map(v => {
      if (v.id === variationId) {
        const newOptions = [...v.options];
         const dragIndex = newOptions.findIndex(o => String(o.id) === draggedOptionId);
        if (dragIndex === -1) return v;

        const [draggedItem] = newOptions.splice(dragIndex, 1);

        // Use current dragOverIndex, fallback to end
        let targetIndex = dragOverIndex ?? newOptions.length;

        // Adjust index if we removed item before target
        if (dragIndex < targetIndex) {
          targetIndex = Math.max(0, targetIndex - 1);
        }

        targetIndex = Math.max(0, Math.min(targetIndex, newOptions.length));
        newOptions.splice(targetIndex, 0, draggedItem);

        // Option data remains the same, only order changes
        return { ...v, options: newOptions };
      }
      return v;
    });
  });

  setDragId(null);
  setDragOverIndex(null);  // Always reset
  setIsDirty(true);
};

// 3. Reset on drag end
const handleOptionDragEnd = () => {
  setDragOverIndex(null);
  setDragId(null);
};

  // Handle input change for preset fields
  const handleVariationInputChange = (variationId, field, value) => {
    setIsDirty(true);
    setInputVariations(prev =>
      prev.map(v =>
        v.id === variationId
          ? { ...v, [field]: value }
          : v
      )
    );
    
    // Clear field error when user starts typing
    if (fieldErrors[variationId]?.[field]) {
      setFieldErrors(prev => ({
        ...prev,
        [variationId]: {
          ...prev[variationId],
          [field]: null
        }
      }));
    }
  };

  // Handle option input key press
const handleOptionInputKeyDown = (e, variationId) => {
  if (e.key === 'Enter') {
    e.preventDefault();
    addNewOption(variationId);
  } else if (e.key === 'Backspace' && !e.target.value) {
    // Remove the last option chip when backspace is pressed on empty input
    setInputVariations(prev => {
      const updatedVariations = prev.map(v => {
        if (v.id === variationId && v.options.length > 0) {
          const lastOption = v.options[v.options.length - 1];
          const newOptionData = { ...v.optionData };
          delete newOptionData[lastOption.id];
          
          const newOptions = v.options.slice(0, -1);
          
          return {
            ...v,
            options: newOptions,
            optionData: newOptionData
          };
        }
        return v;
      });
      
      // Check if removing this option resolved duplicate issues
      const updatedVariation = updatedVariations.find(v => v.id === variationId);
      if (updatedVariation) {
        const optionValues = updatedVariation.options.map(opt => opt.value.toLowerCase());
        const uniqueValues = new Set(optionValues);
        const hasDuplicateOptions = optionValues.length !== uniqueValues.size;
        
        if (!hasDuplicateOptions && fieldErrors[variationId]?.options === 'There are duplicated values') {
          // Clear the duplicate error
          setFieldErrors(prev => ({
            ...prev,
            [variationId]: {
              ...prev[variationId],
              options: null,
              duplicateOptions: null
            }
          }));
        }
      }
      
      return updatedVariations;
    });
    
    setIsDirty(true);
  }
};

  // FIXED: Helper function to get preset data by ID
  const getVariationData = (variationId) => {
    return inputVariations.find(v => v.id === variationId);
  };

  // Calculate dynamic height for options container based on content
  const calculateOptionsHeight = (options, hasError = false) => {
    const baseHeight = 45; // Base height for single line
    const tagHeight = 20; // Height of each tag
    const tagGap = 8; // Gap between tags
    const verticalPadding = 16; // 8px top + 8px bottom
    
    // Calculate content height
    let contentHeight = verticalPadding;
    
    if (options.length === 0) {
      // When empty, we have just the input field (20px) + padding
      return Math.max(baseHeight, contentHeight + 20);
    }
    
    // Estimate lines
    let currentLineWidth = 0;
    let lines = 1;
    
    options.forEach(opt => {
      const chipWidth = Math.max(72, (opt.value.length * 7) + 60);
      if (currentLineWidth + chipWidth > 300) {
        lines++;
        currentLineWidth = chipWidth;
      } else {
        currentLineWidth += chipWidth + 8;
      }
    });
    
    // Check if input needs new line
    if (currentLineWidth > 250) {
      lines++;
    }
    
    // Calculate height: tags + gaps between lines
    contentHeight += (lines * tagHeight) + ((lines - 1) * tagGap);
    
    return Math.max(baseHeight, contentHeight);
  };

  // Calculate row height for editing mode
  const calculateRowHeight = (variationData, hasError = false) => {
    if (!variationData) return hasError ? 70 : 45;
    
    const optionsHeight = calculateOptionsHeight(variationData.options, false); // Don't include error height here
    // Add error height separately
    const errorHeight = hasError ? 20 : 0;
    return Math.max(45, optionsHeight + errorHeight);
  };

  const displayData = sortedFiltered;

  // Check if we should show table or empty state
  const showTable = (rows.length > 0 || isAdding) && (searchTerm ? totalCount > 0 : true);

  // Search must stay enabled whenever the user is actively searching, even if
  // that search yields zero results — otherwise they cannot clear it. (ERP-5005)
  const isSearching = searchTerm.trim().length > 0;
  const isTableEmpty = totalCount === 0 && !isSearching && !isAdding;

  return (
    <div className="company-body new-look">
      {error && (
        <div
          className="d-flex align-items-center justify-content-between"
          role="alert"
          style={errorBoxStyle}
        >
          <div className="d-flex align-items-center">
            <X className="me-2" />
            <strong>{error.message}</strong>
          </div>
          <X size={16} style={{ cursor: "pointer" }} onClick={() => setError(null)} />
        </div>
      )}

      <div className="breadcrumb">
        {breadcrumbs.map((crumb, idx) => (
          <span key={idx} className="breadcrumb-item">
            {crumb}{idx < breadcrumbs.length - 1 && ' '}
          </span>
        ))}
      </div>

      <div className="info-section">
        <div className="info-card new-info-overflow-auto main-company-body-inner">
          <div className="entities-tab">
            <div className="toolbar">
              <div className="toolbar-left">
                <SearchBox
                  searchInput={searchInputValue}
                  setSearchInput={setSearchInputValue}
                  setSearchTerm={setSearchTerm}
                  setCurrentPage={setCurrentPage}
                  disabled={isTableEmpty}
                />
              </div>
              <div className="toolbar-right">
                <button className="icon-btn dark-border" onClick={() => startEdit(null)}>
                  <Plus size={18} /> Add SKU Preset
                </button>
              </div>
            </div>

            {/* Toast Notification */}
            {showToast && (
              <AlertNotification
                type={toastData.type}
                recordName={toastData.recordName}
                action={toastData.action}
                onClose={() => setShowToast(false)}
              />
            )}

            {showTable ? (
              <>
                <style>{`
                  .col-resize-handle::after {
                    content: "";
                    position: absolute;
                    top: 50%;
                    transform: translateY(-50%);
                    height: 50%;
                    left: 3px;
                    width: 2px;
                    background: #DEE2E6;
                  }
                `}</style>
                <div className="table-wrapper">
                  <table className="entities-table" style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                      <col style={{ width: 80 }} />
                      <col style={{ width: colWidths.variation }} />
                      <col style={{ width: colWidths.options }} />
                      <col style={{ width: 100 }} />
                    </colgroup>
                    <thead>
                      <tr>
                        {/* Delete column header */}
                        <th style={{
                          textAlign: 'center',
                          width: '80px',
                          minWidth: '80px',
                          maxWidth: '80px'
                        }}></th>
                        <th
                          onClick={() => handleSort('variation')}
                          style={{
                            cursor: 'pointer',
                            position: 'relative',
                            overflow: 'hidden',
                          }}
                        >
                          <span style={{fontWeight:700}}>SKU Variation
                            <SortIcon direction={sortConfig.key === 'variation' ? sortConfig.direction : 'none'} />
                          </span>
                          <ColResizeHandle colKey="variation" minWidth={120} />
                        </th>
                        <th style={{
                          position: 'relative',
                          overflow: 'hidden',
                        }}>
                          <span style={{fontWeight:700}}>Variation Options</span>
                          <ColResizeHandle colKey="options" minWidth={250} />
                        </th>
                        <th style={{
                          textAlign: 'center',
                          width: '100px',
                          minWidth: '100px',
                          maxWidth: '100px'
                        }}><span style={{fontWeight:700}}>Action</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      {isAdding && inputVariations.filter(v => newRowIds.includes(v.id)).map((variation, index) => {
                        const hasError = fieldErrors[variation.id]?.variation || fieldErrors[variation.id]?.options;
                        const rowHeight = calculateRowHeight(variation, hasError);
                        const rowBgColor = index % 2 === 0 ? '#FFFFFF' : '#F8F9FA';
                        
                        return (
                          <tr key={variation.id} style={{ 
                            backgroundColor: rowBgColor,
                            borderBottom: hasError ? '1px solid #F04438' : '1px solid #E9ECEF'
                          }}>
                            {/* Empty delete column for new rows */}
                            <td style={{ 
                              textAlign: 'center',
                              width: '80px',
                              minWidth: '80px',
                              maxWidth: '80px',
                              verticalAlign: 'top',
                              paddingTop: '15px',
                              paddingBottom: '15px'
                            }}></td>
                            <td style={{
                              verticalAlign: 'top',
                              paddingTop: '15px',
                              paddingBottom: '15px',
                              overflow: 'hidden'
                            }}>
                              <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '4px',
                                rowGap:'4px',
                                //height: rowHeight,
                                justifyContent: hasError ? 'flex-start' : 'center',
                                position: 'relative'
                              }}>
                                <input
                                  className="form-control common-input-all"
                                  value={variation.variation || ''}
                                  onChange={e => handleVariationInputChange(variation.id, 'variation', e.target.value)}
                                  onBlur={() => validateVariationOnBlur(variation.id)}
                                  style={{
                                    borderColor: fieldErrors[variation.id]?.variation ? '#F04438' : '#CED4DA',
                                    height: '45px',
                                    minHeight: '45px',
                                    maxHeight: '45px',
                                    padding: '10px 12px',
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    lineHeight: 'normal',
                                    position: 'relative',
                                    zIndex: 1
                                  }}
                                />
                                {fieldErrors[variation.id]?.variation && (
                                  <div style={{ 
                                    color: '#F04438', 
                                    fontSize: '12px',
                                    marginTop: '4px',
                                    lineHeight: '1.2',
                                    position: 'relative',
                                    width: '100%',
                                    wordBreak: 'break-word'
                                  }}>
                                    {fieldErrors[variation.id].variation}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td style={{
                              position: 'relative',
                              verticalAlign: 'top',
                              paddingTop: '15px',
                              paddingBottom: '15px',
                              overflow: 'hidden'
                            }}>
                              <div 
                                style={{ 
                                  display: 'flex', 
                                  flexDirection: 'column', 
                                  gap: '4px', 
                                  rowGap:'4px',
                                  //height: rowHeight,
                                  justifyContent: hasError ? 'flex-start' : 'center',
                                  position: 'relative'
                                }}
                                onDragOver={handleOptionDragOver}
                                onDrop={(e) => handleOptionDrop(e, variation.id)}
                                onBlur={() => validateOptionsOnBlur(variation.id)}
                              >
                               <div
                                  className="select-box form-control common-input-all"
                                  style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 8,
                                    alignItems: 'flex-start',
                                    borderColor: fieldErrors[variation.id]?.options ? '#F04438' : '#CED4DA',
                                    minHeight: '45px',
                                    height: 'auto',
                                    padding: '8px 12px',
                                    rowGap: '8px',
                                    position: 'relative',
                                    overflow: 'visible',
                                    boxSizing: 'border-box'
                                  }}
                                  onDragOver={handleOptionDragOver}
                                  onDrop={(e) => handleOptionDrop(e, variation.id)}
                                  onDragLeave={() => setDragOverIndex(null)}
                                >
                                  {variation.options.map((opt, index) => (
                                    <span
                                      key={opt.id}
                                      className="tag"
                                      style={{
                                        ...tagStyle,
                                        cursor: 'move',
                                        opacity: dragId === `${variation.id}-${opt.id}` ? 0.5 : 1,
                                        outline: dragOverIndex === index ? '2px dashed #007bff' : 'none',
                                        outlineOffset: '2px',
                                        position: 'relative',
                                        wordBreak: 'break-all'
                                      }}
                                      draggable
                                      onDragStart={(e) => handleOptionDragStart(e, variation.id, opt.id)}
                                      onDragEnd={handleOptionDragEnd}
                                    >
                                      <GripVertical size={14} style={{ marginRight: 4, color: '#6c757d', flexShrink: 0 }} />
                                      {opt.value}
                                      <X
                                        size={14}
                                        style={{ marginLeft: 6, cursor: 'pointer', flexShrink: 0 }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleOptionRemove(variation.id, opt.id);
                                        }}
                                      />
                                    </span>
                                  ))}
                                  <input
                                    type="text"
                                    ref={el => (optionsInputRefs.current[variation.id] = el)}
                                    className="form-control"
                                    style={{
                                      ...tagInputInline,
                                      flex: 1,
                                      minWidth: '150px',
                                      height: '20px',
                                      minHeight: '20px',
                                      alignSelf: 'flex-start',
                                      marginTop: '0'
                                    }}
                                    value={variation.newOptionInput || ''}
                                    onChange={e => handleVariationInputChange(variation.id, 'newOptionInput', e.target.value)}
                                    onKeyDown={(e) => handleOptionInputKeyDown(e, variation.id)}
                                  />
                                </div>
                                {fieldErrors[variation.id]?.options && (
                                  <div style={{ 
                                    color: '#F04438', 
                                    fontSize: '12px',
                                    marginTop: '4px',
                                    lineHeight: '1.2',
                                    position: 'relative',
                                    width: '100%',
                                    wordBreak: 'break-word'
                                  }}>
                                    {fieldErrors[variation.id].options}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td style={{ 
                              textAlign: 'center',
                              width: '100px',
                              minWidth: '100px',
                              maxWidth: '100px',
                              verticalAlign: 'top',
                              paddingTop: '15px',
                              paddingBottom: '15px'
                            }}>
                              <div style={{ 
                                display: 'flex', 
                                alignItems: hasError ? 'flex-start' : 'center', 
                                //height: rowHeight,
                                justifyContent: 'center',
                                paddingTop: hasError ? '0' : '0'
                              }}>
                                <button 
                                  className="icon-btn shadow-none text-gray" 
                                  onClick={() => saveSpecificVariation(variation.id)}
                                >
                                  <Check size={18} />
                                </button>
                                <button 
                                  className="icon-btn shadow-none text-gray" 
                                  onClick={() => cancelSpecificEdit(variation.id)}
                                >
                                  <X size={18} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {displayData.map((row, index) => {
                        const variationData = getVariationData(row.id);
                        const isRowEditing = isEditing(row.id);
                        const hasError = variationData && (fieldErrors[variationData.id]?.variation || fieldErrors[variationData.id]?.options);
                        const rowHeight = isRowEditing ? calculateRowHeight(variationData, hasError) : (hasError ? 70 : 45);
                        const rowBgColor = index % 2 === 0 ? '#FFFFFF' : '#F8F9FA';
                        
                        return (
                          <tr key={row.id} style={{ 
                            backgroundColor: rowBgColor,
                            borderBottom: hasError ? '1px solid #F04438' : '1px solid #E9ECEF'
                          }}>
                            {/* Delete icon column */}
                            <td style={{ 
                              textAlign: 'center',
                              width: '80px',
                              minWidth: '80px',
                              maxWidth: '80px',
                              verticalAlign: isRowEditing && hasError ? 'top' : 'top',
                              paddingTop: isRowEditing ? '10px' : '10px',
                              paddingBottom: (isRowEditing && hasError) ? '4px' : '10px'
                            }}>
                              {!isRowEditing ? (
                                <button 
                                  className="icon-btn shadow-none text-gray" 
                                  onClick={() => handleDeleteVariation(row.id)}
                                  style={{ color: '#F04438' }}
                                >
                                  <Trash2 size={18} />
                                </button>
                              ) : (
                                <button 
                                  className="icon-btn shadow-none text-gray" 
                                  onClick={() => handleDeleteVariation(row.id)}
                                  style={{ color: '#F04438' }}
                                >
                                  <Trash2 size={18} />
                                </button>
                              )}
                            </td>
                            <td style={{
                              verticalAlign: 'top',
                              paddingTop: '15px',
                              paddingBottom: '15px',
                              overflow: 'hidden'
                            }}>
                              {isRowEditing ? (
                                variationData ? (
                                  <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '4px',
                                    //height: rowHeight,
                                    justifyContent: hasError ? 'flex-start' : 'center',
                                    position: 'relative'
                                  }}>
                                    <input
                                      className="form-control common-input-all"
                                      value={variationData.variation || ''}
                                      onChange={e => handleVariationInputChange(variationData.id, 'variation', e.target.value)}
                                      onBlur={() => validateVariationOnBlur(variationData.id)}
                                      style={{
                                        borderColor: fieldErrors[variationData.id]?.variation ? '#F04438' : '#CED4DA',
                                        height: '45px',
                                        minHeight: '45px',
                                        maxHeight: '45px',
                                        padding: '10px 12px',
                                        width: '100%',
                                        boxSizing: 'border-box',
                                        lineHeight: 'normal',
                                        position: 'relative',
                                        zIndex: 1
                                      }}
                                    />
                                    {fieldErrors[variationData.id]?.variation && (
                                      <div style={{ 
                                        color: '#F04438', 
                                        fontSize: '12px',
                                        marginTop: '4px',
                                        lineHeight: '1.2',
                                        position: 'relative',
                                        width: '100%',
                                        wordBreak: 'break-word'
                                      }}>
                                        {fieldErrors[variationData.id].variation}
                                      </div>
                                    )}
                              </div>
                                ) : (
                                  <div style={{
                                    //height: rowHeight,
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '8px 0',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                    textOverflow: 'ellipsis'
                                  }}>
                                    {row.variation}
                                  </div>
                                )
                              ) : (
                                <div style={{
                                  //height: rowHeight,
                                  display: 'flex',
                                  alignItems: 'center',
                                  padding: '8px 0',
                                  overflow: 'hidden',
                                  whiteSpace: 'nowrap',
                                  textOverflow: 'ellipsis'
                                }}>
                                  {row.variation}
                                </div>
                              )}
                            </td>
                            <td style={{
                              position: 'relative',
                              verticalAlign: 'top',
                              paddingTop: '15px',
                              paddingBottom: '15px',
                              overflow: 'hidden'
                            }}>
                              {isRowEditing ? (
                                variationData ? (
                                  <div 
                                    style={{ 
                                      display: 'flex', 
                                      flexDirection: 'column', 
                                      gap: '4px', 
                                      rowGap:'4px',
                                      //height: rowHeight,
                                      justifyContent: hasError ? 'flex-start' : 'center',
                                      position: 'relative'
                                    }}
                                    onDragOver={handleOptionDragOver}
                                    onDrop={(e) => handleOptionDrop(e, variationData.id)}
                                    onBlur={() => validateOptionsOnBlur(variationData.id)}
                                  >
                                    <div
                                      className="select-box form-control common-input-all"
                                      style={{ 
                                        display: 'flex', 
                                        flexWrap: 'wrap', 
                                        gap: 8, 
                                        alignItems: 'flex-start',
                                        borderColor: fieldErrors[variationData.id]?.options ? '#F04438' : '#CED4DA',
                                        minHeight: '45px',
                                        height: 'auto',
                                        padding: '8px 12px',
                                        rowGap: '8px',
                                        boxSizing: 'border-box',
                                        overflow: 'visible'
                                      }}
                                      onDragOver={handleOptionDragOver}
                                      onDrop={(e) => handleOptionDrop(e, variationData.id)}
                                      onDragLeave={() => setDragOverIndex(null)}
                                    >
                                      {variationData.options.map((opt,index) => (
                                        <span
                                          key={opt.id}
                                          className="tag"
                                          style={{ 
                                            ...tagStyle, 
                                            cursor: 'move',
                                            opacity: dragId === `${variationData.id}-${opt.id}` ? 0.5 : 1,
                                            outline: dragOverIndex === index ? '2px dashed #007bff' : 'none',
                                            outlineOffset: '2px',
                                            position: 'relative',
                                            wordBreak: 'break-all' 
                                          }}
                                          draggable
                                          onDragStart={(e) => handleOptionDragStart(e, variationData.id, opt.id)}
                                          onDragOver={handleOptionDragEnd}
                                        >
                                          <GripVertical
                                            size={14}
                                            style={{ marginRight: 4, color: '#6c757d', flexShrink: 0 }}
                                          />
                                          {opt.value}
                                          <X
                                            size={14}
                                            style={{ marginLeft: 6, cursor: 'pointer', flexShrink: 0 }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleOptionRemove(variationData.id, opt.id);
                                            }}
                                          />
                                        </span>
                                      ))}
                                      <input
                                        type="text"
                                        ref={el => (optionsInputRefs.current[variationData.id] = el)}
                                        className="form-control"
                                        style={{
                                          ...tagInputInline,
                                          flex: 1,
                                          minWidth: '150px',
                                          height: '20px',
                                          minHeight: '20px',
                                          alignSelf: 'flex-start',
                                          marginTop: '0'
                                        }}
                                        value={variationData.newOptionInput || ''}
                                        onChange={e => handleVariationInputChange(variationData.id, 'newOptionInput', e.target.value)}
                                        onKeyDown={(e) => handleOptionInputKeyDown(e, variationData.id)}
                                      />
                                    </div>
                                    {fieldErrors[variationData.id]?.options && (
                                      <div style={{ 
                                        color: '#F04438', 
                                        fontSize: '12px',
                                        marginTop: '4px',
                                        lineHeight: '1.2',
                                        position: 'relative',
                                        width: '100%',
                                        wordBreak: 'break-word'
                                      }}>
                                        {fieldErrors[variationData.id].options}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div style={{ 
                                    display: 'flex', 
                                    flexWrap: 'wrap', 
                                    gap: '8px', 
                                    rowGap: '8px', 
                                    minHeight: '45px', 
                                    alignItems: 'center',
                                    alignContent: 'flex-start',
                                    padding: '10px 0'
                                  }}>
                                    {row.options.map(opt => (
                                      <span
                                        key={opt.id}
                                        className="tag"
                                        style={displayTagStyle}
                                      >
                                        {opt.value}
                                      </span>
                                    ))}
                                  </div>
                                )
                              ) : (
                                <div style={{ 
                                  display: 'flex', 
                                  flexWrap: 'wrap', 
                                  gap: '8px', 
                                  rowGap: '8px', 
                                  minHeight: '45px', 
                                  alignItems: 'center',
                                  alignContent: 'flex-start',
                                  padding: '10px 0'
                                }}>
                                  {row.options.map(opt => (
                                    <span
                                      key={opt.id}
                                      className="tag"
                                      style={displayTagStyle}
                                    >
                                      {opt.value}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </td>
                            <td style={{ 
                              textAlign: 'center',
                              width: '100px',
                              minWidth: '100px',
                              maxWidth: '100px',
                              verticalAlign: 'top',
                              paddingTop: '15px',
                              paddingBottom: '15px'
                            }}>
                              {isRowEditing ? (
                                variationData ? (
                                  <div style={{ 
                                    display: 'flex', 
                                    alignItems: hasError ? 'flex-start' : 'center', 
                                    //height: rowHeight,
                                    justifyContent: 'center',
                                    paddingTop: hasError ? '0' : '0'
                                  }}>
                                    <button 
                                      className="icon-btn shadow-none text-gray" 
                                      onClick={() => saveSpecificVariation(variationData.id)}
                                    >
                                      <Check size={18} />
                                    </button>
                                    <button 
                                      className="icon-btn shadow-none text-gray" 
                                      onClick={() => cancelSpecificEdit(variationData.id)}
                                    >
                                      <X size={18} />
                                    </button>
                                  </div>
                                ) : null
                              ) : (
                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                  <button 
                                    className="icon-btn shadow-none text-gray" 
                                    onClick={() => startEdit(row)}
                                  >
                                    <Pencil size={18} />
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination controls */}
                {totalCount > 0 && (
                  <div className="pagination">
                    <div className="pagination-info">
                      {`${(currentPage - 1) * PAGE_SIZE + 1} - ${Math.min(currentPage * PAGE_SIZE, totalCount)} of ${totalCount}`}
                    </div>
                    <Pagination
                      currentPage={currentPage}
                      totalPages={Math.ceil(totalCount / PAGE_SIZE)}
                      onPageChange={setCurrentPage}
                    />
                  </div>
                )}
              </>
            ) : (
              // Empty state when no presets exist or search returns no results
              <div style={{ 
                textAlign: 'center', 
                padding: '60px 20px',
                color: '#6C757D',
                fontSize: '14px',
                width: '100%',
                minHeight: '200px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {searchTerm ? (<div className="empty-state">
                    <div className="empty-state-content">
                        <div className="icon"><img
                            src={emptySearchImg}
                            alt="avatar"
                            className="avatar"
                            style={{
                                width: 120,
                                height: 120,
                                verticalAlign: 'middle'
                            }}
                        /></div>
                        <h1 className="no-item-title mt-2">
                            No SKU Presets Found
                        </h1>
                        <p className="no-item-text">
                            No results match what you're looking for
                        </p>
                        
                    </div>
                </div>) :
              (
                <div className="empty-state">
                    <div className="empty-state-content">
                        <div className="icon"><img
                            src={noBrandImage}
                            alt="avatar"
                            className="avatar"
                            style={{
                                width: 120,
                                height: 120,
                                verticalAlign: 'middle'
                            }}
                        /></div>
                        <h1 className="no-item-title mt-2">
                            No SKU Presets Exist
                        </h1>
                        <p className="no-item-text">
                            Nothing here yet. Add your first item to get started!
                        </p>
                        
                    </div>
                </div>
            )}
              </div>
            )}

            {/* Delete Confirmation Modal */}
            <DeleteConfirmationModal
              isOpen={showDeleteConfirmationModal}
              onCancel={cancelDeleteVariation}
              onConfirm={confirmDeleteVariation}
              entityName="SKU Variation"
            />

            {/* Edit Confirmation Modal */}
            <EditConfirmationModal
              isOpen={showEditConfirmationModal}
              onCancel={cancelEdit}
              onConfirm={confirmEdit}
            />

            {/* Discard Confirmation Modal */}
            <DiscardConfirmationModal
              isOpen={showDiscardModal}
              onCancel={() => {
                setShowDiscardModal(false);
                setPendingVariationId(null);
              }}
              onConfirm={() => {
                if (pendingVariationId) {
                  performSpecificCancel(pendingVariationId);
                }
              }}
              entityName="SKU Variation"
            />

            {/* Navigation Confirmation Modal */}
            <DiscardConfirmationModal
              isOpen={showExitModal}
              onCancel={cancelNavigation}
              onConfirm={confirmNavigation}
              entityName="SKU Variation"
              title="Unsaved Changes"
              message="You have unsaved changes. Are you sure you want to leave?"
              confirmText="Leave"
              cancelText="Stay"
            />

            {showVariationChangeModal && (
              <>
                <div className="drawer-overlay" />
                <div
                  className="entity-drawer modal-popup-cls"
                  style={{
                    maxWidth: 650,
                    margin: "0 auto",
                    background: "white",
                    borderRadius: 12,
                    zIndex: 1050,
                    padding: 24,
                    position: "fixed",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
                    height: "auto",
                    right: "auto",
                    overflow: "inherit",
                  }}
                >
                  <div className="drawer-header d-flex justify-content-end align-items-center pt-0 pe-0">
                    <button className="close-btn" onClick={cancelVariationChange}>
                      <X size={20} />
                    </button>
                  </div>
                  <h3 style={{ textAlign: "left", color: "#212529", fontSize: "20px", fontWeight: "600", lineHeight: "24px", fontFamily: "Montserrat", marginBottom: "5px" }}>
                    Confirm Variation Change
                  </h3>
                  <p style={{ marginBottom: "0", textAlign: "left", color: "#6C757D", fontSize: "14px", fontWeight: "400", lineHeight: "20px", fontFamily: "Montserrat" }}>
                    You are going to change the SKU Variation's text
Once modified, all related texts in ERP will be updated (such as in SPU & SKU module).
                  </p>
                  <div className="drawer-footer" style={{ paddingTop: "10px", display: 'flex', gap: '10px' }}>
                    <button className="btn-save" onClick={confirmVariationChange}>
                      Confirm
                    </button>
                    <button className="btn-cancel" onClick={cancelVariationChange}>
                      Cancel
                    </button>
                  </div>
                </div>
              </>
            )}

            {showDeleteOptionModal && (
              <>
                <div className="drawer-overlay" />
                <div
                  className="entity-drawer modal-popup-cls"
                  style={{
                    maxWidth: 650,
                    margin: "0 auto",
                    background: "white",
                    borderRadius: 12,
                    zIndex: 1050,
                    padding: 24,
                    position: "fixed",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
                    height: "auto",
                    right: "auto",
                    overflow: "inherit",
                  }}
                >
                  <div className="drawer-header d-flex justify-content-end align-items-center pt-0 pe-0">
                    <button className="close-btn" onClick={cancelDeleteOption}>
                      <X size={20} />
                    </button>
                  </div>
                  <h3 style={{ textAlign: "left", color: "#212529", fontSize: "20px", fontWeight: "600", lineHeight: "24px", fontFamily: "Montserrat", marginBottom: "5px" }}>
                    Cannot Delete Option
                  </h3>
                  <p style={{ marginBottom: "0", textAlign: "left", color: "#6C757D", fontSize: "14px", fontWeight: "400", lineHeight: "20px", fontFamily: "Montserrat" }}>
                    This option is referenced by existing SPU/SKU records and cannot be deleted.
                  </p>
                  <div className="drawer-footer" style={{ paddingTop: "10px" }}>
                    <button className="btn-save" onClick={cancelDeleteOption}>
                      OK
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        <UnsavedChangesPrompt isDirty={isDirty} />
      </div>
    </div>
  );
}

const errorBoxStyle = {
  boxShadow: '0px 4px 10px #0000001F',
  padding: '15px 10px',
  borderRadius: '12px',
  border: '1px solid #DC3545',
  backgroundColor: '#F8D7DA',
  color: '#58151C',
  fontSize: '12px',
  position: 'fixed',
  top: '10px',
  width: 'calc(100% - 20px)',
  maxWidth: '665px',
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 2000
};

// Updated tagStyle with new dimensions and white background with yellow border
const tagStyle = {
  backgroundColor: '#FFFFFF',
  color: '#212529',
  borderRadius: '16px',
  padding: '0px 10px',
  fontSize: '12px',
  fontWeight: '600',
  display: 'inline-flex',
  justifyContent:'space-between',
  alignItems: 'center',
  border: '1px solid #FFEB00',
  boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
  minHeight: '20px',
  lineHeight: '20px',
  minWidth: '72px',
  boxSizing: 'border-box',
  alignSelf: 'flex-start',
  marginTop: '0'
};

// New style for display-only tags (non-editable) with new dimensions
const displayTagStyle = {
  backgroundColor: '#FFEB00',
  color: '#212529',
  borderRadius: '16px',
  padding: '0px 10px',
  fontSize: '12px',
  fontWeight: '600',
   display: 'inline-flex',
  justifyContent:'center',
  alignItems: 'center',
  minHeight: '20px',
  lineHeight: '20px',
  minWidth: '72px',
  boxSizing: 'border-box',
  alignSelf: 'flex-start',
  wordBreak: 'break-all'
};

const tagInputInline = {
  height: '20px',
  minHeight: '20px',
  padding: '0 6px',
  fontSize: '13px',
  border: 'none',
  outline: 'none',
  boxShadow: 'none',
  background: 'transparent',
  flexGrow: 1,
  minWidth: '150px',
  alignSelf: 'flex-start',
  lineHeight: '20px',
  marginTop: '4px'
};

